import React, { useCallback, useState, useContext } from 'react';
import { Link as GatsbyLink } from 'gatsby';
import { Link, Badge } from '@printdeal/ui-library';
import {
  useExperiment, useStoreToggle,
} from '@printdeal/feature-management/react';
import { addDataLayerEntry, addEventEntry } from '@printdeal/data-layer-manager';
import { FormattedMessage } from 'react-intl';
import { useLocation } from '@gatsbyjs/reach-router';
import { useSelector } from 'react-redux';
import useSystemPages from '../../../../hooks/useSystemPages';
import LoginDrawerModal from '../../../authentication/login-modal/LoginModal';
import { dispatchDataLayerEventLoginModal, dispatchDataLayerEventLoginModalToggle } from '../../../../helpers/dataLayerHelpers/LoginDataLayerHelper';
import { selectors as authSelectors } from '../../../../store/slices/authSlice';
import { HeaderContext } from '../context';
import Exp1517ShowMyAccountItems from './Exp1517ShowMyAccountItems';

const LoggedOutNavigation = () => {
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loginPage] = useSystemPages('login');
  const isLoggedIn = useSelector(authSelectors.isLoggedIn);
  const { isSticky } = useContext(HeaderContext);

  const { experiment, isInitialized } = useExperiment('hammerheads_exp1517_myaccount_items_before_login');
  const isExp1517Active = isInitialized && experiment.variation === 'variant';

  const isStoreToggleEnabled = useStoreToggle('drukwerkdeal-nl').featureToggle.isEnabled;

  const loginButtonClickEventGA = useCallback(() => {
    const eventCategory = isSticky ? 'header_login_sticky' : 'header_login';
    const eventAction = 'click';
    addDataLayerEntry(
      eventCategory,
      {
        eventAction,
      },
    );
    addEventEntry({
      eventCategory,
      eventAction,
    });
    if (isExp1517Active) {
      addEventEntry({
        eventCategory: 'account',
        eventAction: 'modal_view',
      });
    }
  }, [isSticky, isExp1517Active]);

  const handleSetIsModalOpen = useCallback((state: boolean) => {
    setIsModalOpen(state);
    dispatchDataLayerEventLoginModalToggle(location.pathname, state);
  }, [location]);

  const handleLoginClick = useCallback((e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    if (!isLoggedIn) loginButtonClickEventGA();

    setIsModalOpen(!isModalOpen);
    dispatchDataLayerEventLoginModal(location.pathname);
  }, [isLoggedIn, loginButtonClickEventGA, isModalOpen, location]);

  if (isExp1517Active) {
    return (
      <>
        <Link
          as={GatsbyLink}
          to={loginPage.path}
          onClick={handleLoginClick}
          variant="secondary"
          iconBefore="user"
          className="pd-flex"
          data-cy="headerLoginLink"
        >
          <span className="pd-sr-only lg:pd-not-sr-only">
            <FormattedMessage id="header.account.title" defaultMessage="My account" />
          </span>
        </Link>
        <Exp1517ShowMyAccountItems isModalOpen={isModalOpen} setIsModalOpen={handleSetIsModalOpen} />
      </>
    );
  }

  return (
    <>
      <Link
        as={GatsbyLink}
        to={loginPage.path}
        onClick={handleLoginClick}
        variant="secondary"
        iconBefore="user"
        className="pd-relative pd-flex"
        data-cy="headerLoginLink"
      >
        {isStoreToggleEnabled && (
          <Badge variant="primary-circle" className="pd-absolute -pd-top-3 pd-left-3">
            &bull;
          </Badge>
        )}
        <span className="pd-sr-only lg:pd-not-sr-only">
          {loginPage.title}
        </span>
      </Link>
      <LoginDrawerModal isModalOpen={isModalOpen} setIsModalOpen={handleSetIsModalOpen} />
    </>
  );
};

export default LoggedOutNavigation;
